import { Box, Text, Grid, GridItem, Button } from '@everlywell/ui-kit';
import Price from 'components/Price';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ProductVariant } from 'utils/types';

import FallbackProductImage from '../CartLineItem/assets/Fallback-At-Home-Lab-Test.png';
import QuantityControls from './components/QuantityControls';

export type CartUpsellProps = {
  productVariant: Pick<ProductVariant, 'name' | 'price' | 'metaDescription'>;
  onAddToCart: () => void;
  isEnabled?: boolean;
  productImage?: IGatsbyImageData;
  isAddingToCart?: boolean;
  hasQuantityControls?: boolean;
  quantity?: number;
  onIncrement?: () => void;
  onDecrement?: () => void;
  onRemove?: () => void;
};

/**
 * Upsell card for the cart experience.
 */
function CartUpsell(props: CartUpsellProps): JSX.Element {
  const {
    productVariant,
    onAddToCart,
    isEnabled = true,
    productImage,
    isAddingToCart = false,
    hasQuantityControls = false,
    quantity = 0,
    onIncrement = () => {},
    onDecrement = () => {},
    onRemove = () => {},
  } = props;

  const { name: productName, price, metaDescription } = productVariant;

  const handleAddToCart = () => {
    onAddToCart();
    analytics.track({
      event: ANALYTICS.EVENTS.ADD_TO_CART,
      data: {
        label: 'Cart V2 - Upsell',
        product: productName,
      },
    });
  };

  const productPriceNumber = parseFloat(price ?? '0');

  useEffect(() => {
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
      data: {
        label: 'Cart V2 - Upsell',
        product: productName,
      },
    });
  }, [productName]);

  return (
    <Box
      padding={4}
      marginBottom={4}
      marginTop={0}
      background="transparent"
      borderRadius="lg"
      borderColor="tints.darkCream"
      borderWidth={'1px'}
      borderStyle="solid"
    >
      <Grid spacing={4}>
        <GridItem width={1 / 5}>
          {productImage ? (
            <Box as={GatsbyImage} alt="" image={productImage} />
          ) : (
            <Box as="img" alt="" src={FallbackProductImage} />
          )}
        </GridItem>

        <GridItem width={4 / 5}>
          <Box
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            height="100%"
            gap={2}
          >
            <Box>
              <Text size="xl">{productName}</Text>
            </Box>

            <Box>
              <Text size="sm" color="tints.darkGrey">
                {metaDescription}
              </Text>
            </Box>

            <Box>
              <Grid
                spacing={4}
                justifyContent="space-between"
                alignItems={[null, 'center']}
                flexDirection={['row']}
              >
                <GridItem width="auto">
                  <Box display="flex" gap={2} alignItems="baseline">
                    <Price price={productPriceNumber} fontSize="lg" />
                  </Box>
                </GridItem>
                <GridItem
                  width="auto"
                  display="flex"
                  gap={2}
                  alignItems="flex-end"
                  sx={{
                    flexGrow: 0,
                  }}
                >
                  {hasQuantityControls && quantity >= 1 ? (
                    <QuantityControls
                      quantity={quantity}
                      onIncrement={onIncrement}
                      onDecrement={onDecrement}
                      onRemove={onRemove}
                    />
                  ) : (
                    <Button
                      variant="primary"
                      colorScheme="greenCS"
                      size="sm"
                      aria-label="Add to cart"
                      onClick={handleAddToCart}
                      isDisabled={!isEnabled}
                      isLoading={isAddingToCart}
                    >
                      Add to cart
                    </Button>
                  )}
                </GridItem>
              </Grid>
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
}

export default CartUpsell;
